import { Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { updateFlatsTableConfig } from '../../../api'
import { EMPTY_OBJECT } from '../../../constants/common'
import { FLAT_ENTITY } from '../../../constants/entity'
import { SUBMENU_PARAM_ACTIVE_VALUE } from '../../../constants/route'
import {
  useModals,
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import Button from '../../Button'
import { FILTER_BAR_FILTER_TRIGGER_VARIANT, FilterBar } from '../../Filter'
import {
  FLAT_TABLE_MODALS_CONFIG,
  FlatTableLayout,
  getFlatTableItemActions,
  getFlatTableMassActions,
  useFlatTableActions,
  useFlatTablePage,
} from '../../Flat'
import { PageActions, PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { TablePagination } from '../../TablePagination'

import { QA_NAME_ENTITY_TAB_FLATS_ADD_NEW } from './EntityTabFlats.constants'
import {
  getEntityTabFlatsExcludeFilters,
  getEntityTabFlatsScopeFilter,
} from './EntityTabFlats.utils'

import * as styles from './EntityTabFlats.module.css'

import FlatIcon from '../../../icons/flat.svg'
import PlusIcon from '../../../icons/plus.svg'

function EntityTabFlats({
  entity = FLAT_ENTITY,
  filterStateApi,
  isLocalFilterState,
  qaName = QA_NAME_ENTITY_TAB_FLATS_ADD_NEW,
  scopeFilterKey,
  scopeId,
  shouldShowCreateAction = true,
  onApplyTableConfig = updateFlatsTableConfig,
  onCreate,
  onEntityReload,
  onRegisterReload,
  onReloadCounters,
}) {
  const { t } = useTranslation()

  const {
    closeEmailFormModal,
    closeModal,
    modals,
    setEmailFormModal,
    setModal,
  } = useModals(FLAT_TABLE_MODALS_CONFIG)

  const scopeFilters = scopeFilterKey
    ? getEntityTabFlatsScopeFilter({ scopeFilterKey, scopeId })
    : null

  const scopeSingleFieldApiParams = scopeFilterKey
    ? { [scopeFilterKey]: scopeId }
    : EMPTY_OBJECT

  const tablePage = useFlatTablePage({
    asyncTableOptions: {
      extraFilters: scopeFilters,
      filterStateApi,
      isLocalFilterState,
      withoutSubMenu: true,
    },
    entity,
    extraSelectionResetDeps: [scopeId],
    isBrowserTabTitleEnabled: false,
    sub: SUBMENU_PARAM_ACTIVE_VALUE,
    onApplyTableConfig,
  })

  const {
    availableFilters,
    excludeIds,
    filterValues,
    handleChangeShouldReloadFilters,
    handleCleanSelection,
    handleLoadInit,
    handleReload,
    handleReloadTableAfterMassAction,
    handleSetFilterValues,
    handleSetPagination,
    handleUnselectAllPages,
    hasFilterChanged,
    isInitialLoadSettled,
    isLoading,
    isRefetching,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    selectAllPages,
    selectedIds,
    shouldReloadFilters,
  } = tablePage

  async function handleReloadAfterMassAction() {
    onEntityReload?.()

    return handleReloadTableAfterMassAction()
  }

  const { isPerformingMassAction, setItemAction, setMassAction } =
    useFlatTableActions({
      excludeIds,
      filterValues: { ...filterValues, ...scopeFilters },
      pagination,
      selectAllPages,
      selectedIds,
      sub: SUBMENU_PARAM_ACTIVE_VALUE,
      unselectAllPages: handleUnselectAllPages,
      onCleanSelection: handleCleanSelection,
      onCloseEmailFormModal: closeEmailFormModal,
      onCloseModal: closeModal,
      onReload: handleReloadAfterMassAction,
      onSetEmailFormModal: setEmailFormModal,
      onSetModal: setModal,
    })

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReload,
    onReloadCounters,
  })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [scopeId])

  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  function handleAddFlat() {
    onCreate?.({ onReloadList: handleReload })
  }

  const massActions = getFlatTableMassActions({
    permissions,
    sub: SUBMENU_PARAM_ACTIVE_VALUE,
    t,
  })

  const hasFlats = items?.items?.length > 0

  const hasTable = hasFlats || isLoading

  const shouldCollapseToolbar = !hasFlats && !hasFilterChanged && !isLoading

  const shouldShowCreateFlatAction =
    shouldShowCreateAction && (permissions.can_create || isLoading)

  const emptyStateActions = getEmptyStateActions({
    actionName: qaName,
    isLoading,
    qaName,
    shouldShowActions: shouldShowCreateFlatAction && !hasFilterChanged,
    text: t('FlatTable.AddNewBtnKey'),
    onClick: handleAddFlat,
  })

  return (
    <div className={styles.EntityTabFlats} ref={pageScrollWrapperRef}>
      {isPerformingMassAction || isRefetching ? <PageLoader /> : null}
      {shouldCollapseToolbar ? null : (
        <div className={styles.toolbar}>
          <FilterBar
            responsive
            availableFilters={availableFilters}
            className={styles.filterBar}
            entity={entity}
            excludeFilters={getEntityTabFlatsExcludeFilters(scopeFilterKey)}
            filterTriggerVariant={
              FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON
            }
            filterValues={filterValues}
            isSkeleton={isLoading}
            ordering={ordering}
            pagination={{ per_page: pagination?.pageSize }}
            setFilterValues={handleSetFilterValues}
            shouldReloadValues={shouldReloadFilters}
            singleFieldApiParams={scopeSingleFieldApiParams}
            sub={SUBMENU_PARAM_ACTIVE_VALUE}
            onChangeShouldReloadValues={handleChangeShouldReloadFilters}
            onReload={handleReload}
          />
          <PageActions className={styles.actions} isLoading={isLoading}>
            {shouldShowCreateFlatAction ? (
              <Button.Text
                beforeIconProps={{ Icon: PlusIcon }}
                isSkeleton={isLoading}
                qaName={qaName}
                text={t('FlatTable.AddNewBtnKey')}
                onClick={handleAddFlat}
              />
            ) : null}
          </PageActions>
        </div>
      )}
      <FlatTableLayout
        emptyState={
          <PageEmptyState
            EntityIcon={FlatIcon}
            actions={emptyStateActions}
            className={styles.emptyPage}
            text={t('Flat.NoDataKey')}
          />
        }
        extraModals={modals.map(([id, element]) => (
          <Fragment key={id}>{element}</Fragment>
        ))}
        getFlatItemActions={getFlatTableItemActions}
        hasTable={hasTable}
        massActions={massActions}
        setItemAction={setItemAction}
        setMassAction={setMassAction}
        tablePage={tablePage}
      />
      {hasTable ? (
        <TablePagination
          responsive
          className={styles.pagination}
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      ) : null}
    </div>
  )
}

// This module is reachable from the Email barrel (via RequestProfile tabs), so it
// must not read Email exports at module scope — consumers apply withDraftProviders
// with EmailDraftTableProvider themselves to avoid a circular import crash.
export default EntityTabFlats
